import { AlertCircle } from "@tamagui/lucide-icons";
import {
  TemperatureUnit,
  WeatherAirQualityLevel,
  WeatherConditions,
  WeatherRelativeHumidityLevel,
  WeatherUvIndexLevel,
} from "common";
import { format } from "date-fns";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Heading,
  Text,
  Token,
  View,
  XStack,
  YStack,
  getTokenValue,
  ColorTokens,
} from "tamagui";

import { BorderedCard, BorderedCardProps } from "./BorderedCard";
import { LineChart, LineChartDataItem } from "./LineChart";
import { GlowTriple } from "../components/GlowTriple";
import { WeatherConditionsImage } from "../components/WeatherConditionsImage";
import { WeatherConditionsText } from "../components/WeatherConditionsText";
import { WeatherDayForecast } from "../types/Weather";

export const WeatherHighLowCard = ({
  value,
  conditions,
  unit,
  high,
  low,
  ...rest
}: Omit<BorderedCardProps, "title" | "icon"> & {
  value: number;
  conditions: WeatherConditions;
  unit: TemperatureUnit;
  high: number;
  low: number;
}) => {
  const { t } = useTranslation();

  const temperatureKey =
    unit === TemperatureUnit.Fahrenheit
      ? "temperatureFahrenheit"
      : "temperatureCelsius";

  return (
    <BorderedCard title={t("weather.todayHighLow")} {...rest}>
      <YStack flexGrow={1} gap="$3.5">
        <XStack justifyContent="space-between" alignItems="center">
          <YStack>
            <Heading fontSize="$4" lineHeight="$3">
              {t(`weather.${temperatureKey}`, { value })}
            </Heading>
            <WeatherConditionsText conditions={conditions} />
          </YStack>
          <WeatherConditionsImage conditions={conditions} size={80} hasGlow />
        </XStack>
        <View
          flexGrow={1}
          backgroundColor="$transparentBorder"
          padding="$3.5"
          borderRadius="$6"
        >
          <YStack height="100%" gap="$3.5" justifyContent="space-evenly">
            <XStack justifyContent="space-between">
              <Text>{t("weather.lowest")}</Text>
              <Text fontWeight="bold">
                {t(`weather.${temperatureKey}`, { value: low })}
              </Text>
            </XStack>
            <XStack justifyContent="space-between">
              <Text>{t("weather.highest")}</Text>
              <Text fontWeight="bold">
                {t(`weather.${temperatureKey}`, { value: high })}
              </Text>
            </XStack>
          </YStack>
        </View>
      </YStack>
    </BorderedCard>
  );
};

export const WeatherForecastCard = ({
  value,
  conditions,
  unit,
  forecast,
  ...rest
}: Omit<BorderedCardProps, "title" | "icon"> & {
  value: number;
  conditions: WeatherConditions;
  unit: TemperatureUnit;
  forecast: WeatherDayForecast[];
}) => {
  const { t } = useTranslation();

  const temperatureKey =
    unit === TemperatureUnit.Fahrenheit
      ? "temperatureFahrenheit"
      : "temperatureCelsius";

  return (
    <BorderedCard title={t("weather.upcomingWeather")} {...rest}>
      <YStack flexGrow={1} gap="$3.5">
        <XStack justifyContent="space-between" alignItems="center">
          <YStack>
            <Heading fontSize="$4" lineHeight="$3">
              {t(`weather.${temperatureKey}`, { value })}
            </Heading>
            <WeatherConditionsText conditions={conditions} />
          </YStack>
          <WeatherConditionsImage conditions={conditions} size={80} hasGlow />
        </XStack>
        <View
          flexGrow={1}
          backgroundColor="$transparentBorder"
          padding="$3.5"
          borderRadius="$6"
        >
          <XStack height="100%" gap="$3.5" justifyContent="space-evenly">
            {forecast.map(({ date, temperature, conditions }, index) => (
              <YStack key={index} gap="$2.5" alignItems="center">
                <Text fontWeight={index === 0 ? "bold" : undefined}>
                  {format(date, "EEE")}
                </Text>
                <WeatherConditionsImage conditions={conditions} size={36} />
                <Text fontWeight="bold">
                  {t(`weather.${temperatureKey}`, { value: temperature })}
                </Text>
              </YStack>
            ))}
          </XStack>
        </View>
      </YStack>
    </BorderedCard>
  );
};

export const WeatherUvIndexCard = ({
  value,
  level,
  ...rest
}: Omit<BorderedCardProps, "title" | "icon"> & {
  value: number;
  level: WeatherUvIndexLevel;
}) => {
  const { t } = useTranslation();

  const titleTextMap = {
    [WeatherUvIndexLevel.Low]: t("weather.uvIndexLow", { value }),
    [WeatherUvIndexLevel.Moderate]: t("weather.uvIndexModerate", { value }),
    [WeatherUvIndexLevel.High]: t("weather.uvIndexHigh", { value }),
    [WeatherUvIndexLevel.VeryHigh]: t("weather.uvIndexVeryHigh", { value }),
    [WeatherUvIndexLevel.Extreme]: t("weather.uvIndexExtreme", { value }),
  };
  const descriptionTextMap = {
    [WeatherUvIndexLevel.Low]: t("weather.uvIndexLowDescription"),
    [WeatherUvIndexLevel.Moderate]: t("weather.uvIndexModerateDescription"),
    [WeatherUvIndexLevel.High]: t("weather.uvIndexHighDescription"),
    [WeatherUvIndexLevel.VeryHigh]: t("weather.uvIndexVeryHighDescription"),
    [WeatherUvIndexLevel.Extreme]: t("weather.uvIndexExtremeDescription"),
  };
  const conditionsMap = {
    [WeatherUvIndexLevel.Low]: WeatherConditions.Cloudy,
    [WeatherUvIndexLevel.Moderate]: WeatherConditions.SunCloudy,
    [WeatherUvIndexLevel.High]: WeatherConditions.PartlyCloudyDaytime,
    [WeatherUvIndexLevel.VeryHigh]: WeatherConditions.Sunny,
    [WeatherUvIndexLevel.Extreme]: WeatherConditions.HighTemperature,
  };
  const iconMap = {
    [WeatherUvIndexLevel.Low]: null,
    [WeatherUvIndexLevel.Moderate]: null,
    [WeatherUvIndexLevel.High]: null,
    [WeatherUvIndexLevel.VeryHigh]: <AlertCircle color="$warningRed" />,
    [WeatherUvIndexLevel.Extreme]: <AlertCircle color="$warningRed" />,
  };

  const titleText = titleTextMap[level];
  const descriptionText = descriptionTextMap[level];
  const conditions = conditionsMap[level];
  const icon = iconMap[level];

  return (
    <BorderedCard title={t("weather.uvIndex")} icon={icon} {...rest}>
      <YStack flexGrow={1} gap="$4" alignItems="center">
        <YStack
          flexGrow={1}
          gap="$2.5"
          alignItems="center"
          justifyContent="center"
        >
          <WeatherConditionsImage conditions={conditions} size={80} hasGlow />
          <Heading fontSize="$4" lineHeight="$3" zIndex={1}>
            {titleText}
          </Heading>
        </YStack>
        <Text fontSize="$3.5">{descriptionText}</Text>
      </YStack>
    </BorderedCard>
  );
};

export const WeatherExternalAirQualityCard = ({
  value,
  level,
  ...rest
}: Omit<BorderedCardProps, "title" | "icon"> & {
  value: number;
  level: WeatherAirQualityLevel;
}) => {
  const { t } = useTranslation();

  const titleTextMap = {
    [WeatherAirQualityLevel.Good]: t("weather.externalAirQualityGood"),
    [WeatherAirQualityLevel.Moderate]: t("weather.externalAirQUalityModerate"),
    [WeatherAirQualityLevel.Unhealthy]: t(
      "weather.externalAirQualityUnhealthy",
    ),
    [WeatherAirQualityLevel.VeryUnhealthy]: t(
      "weather.externalAirQualityVeryUnhealthy",
    ),
  };
  const glowColorMap = {
    [WeatherAirQualityLevel.Good]: "$successGreen" as ColorTokens,
    [WeatherAirQualityLevel.Moderate]: "$glowOrange" as ColorTokens,
    [WeatherAirQualityLevel.Unhealthy]: "$glowRed" as ColorTokens,
    [WeatherAirQualityLevel.VeryUnhealthy]: "$glowRed" as ColorTokens,
  };
  const iconMap = {
    [WeatherAirQualityLevel.Good]: null,
    [WeatherAirQualityLevel.Moderate]: null,
    [WeatherAirQualityLevel.Unhealthy]: <AlertCircle color="$warningRed" />,
    [WeatherAirQualityLevel.VeryUnhealthy]: <AlertCircle color="$warningRed" />,
  };

  const titleText = titleTextMap[level];
  const glowColor = glowColorMap[level];
  const icon = iconMap[level];

  return (
    <BorderedCard title={t("weather.externalAirQuality")} icon={icon} {...rest}>
      <YStack flexGrow={1} gap="$4" alignItems="center">
        <View
          flexGrow={1}
          alignItems="center"
          justifyContent="center"
          position="relative"
        >
          <View position="absolute">
            <GlowTriple gradientColors={[glowColor, glowColor, glowColor]} />
          </View>
          <YStack gap="$2.5" alignItems="center" zIndex={0}>
            <Heading fontSize="$6" lineHeight="$3">
              {value}
            </Heading>
            <Heading fontSize="$6" lineHeight="$3">
              {titleText}
            </Heading>
          </YStack>
        </View>
        <Text fontSize="$3.5">
          {t("weather.externalAirQualityDescription")}
        </Text>
      </YStack>
    </BorderedCard>
  );
};

export const WeatherExternalHumidityCard = ({
  value,
  level,
  forecast,
  ...rest
}: Omit<BorderedCardProps, "title" | "icon"> & {
  value: number;
  level: WeatherRelativeHumidityLevel;
  forecast: WeatherDayForecast[];
}) => {
  const { t } = useTranslation();

  const titleTextMap = {
    [WeatherRelativeHumidityLevel.Dry]: t("weather.externalHumidityDry"),
    [WeatherRelativeHumidityLevel.Normal]: t("weather.externalHumidityNormal"),
    [WeatherRelativeHumidityLevel.SlightlyHumid]: t(
      "weather.externalHumiditySlightlyHumid",
    ),
    [WeatherRelativeHumidityLevel.Humid]: t("weather.externalHumidityHumid"),
    [WeatherRelativeHumidityLevel.VeryHumid]: t(
      "weather.externalHumidityVeryHumid",
    ),
  };
  const graphColorMap = {
    [WeatherRelativeHumidityLevel.Dry]: "$humidityGlowWarning" as Token,
    [WeatherRelativeHumidityLevel.Normal]: "$hgPrimary" as Token,
    [WeatherRelativeHumidityLevel.SlightlyHumid]:
      "$humidityGlowWarning" as Token,
    [WeatherRelativeHumidityLevel.Humid]: "$humidityGlowCritical" as Token,
    [WeatherRelativeHumidityLevel.VeryHumid]: "$humidityGlowCritical" as Token,
  };
  const iconMap = {
    [WeatherRelativeHumidityLevel.Dry]: null,
    [WeatherRelativeHumidityLevel.Normal]: null,
    [WeatherRelativeHumidityLevel.SlightlyHumid]: null,
    [WeatherRelativeHumidityLevel.Humid]: <AlertCircle color="$warningRed" />,
    [WeatherRelativeHumidityLevel.VeryHumid]: (
      <AlertCircle color="$warningRed" />
    ),
  };

  const titleText = titleTextMap[level];
  const graphColor = getTokenValue(graphColorMap[level], "color");
  const icon = iconMap[level];

  const graphData = useMemo(
    () => [
      {
        label: format(new Date(), "EEE"),
        value,
      },
      ...forecast.map(
        ({ date, relativeHumidity }) =>
          ({
            label: format(date, "EEE"),
            value: relativeHumidity,
          }) as LineChartDataItem,
      ),
    ],
    [value, forecast],
  );

  return (
    <BorderedCard title={t("weather.externalHumidity")} icon={icon} {...rest}>
      <YStack gap="$3.5">
        <YStack>
          <Heading fontSize="$4" lineHeight="$3">
            {t(`weather.relativeHumidity`, { value })}
          </Heading>
          <Text>{titleText}</Text>
        </YStack>
        <LineChart
          data={graphData}
          unit="%"
          minValue={0}
          maxValue={100}
          yAxisLabelWidth={35}
          yAxisLabelDecimals={0}
          chartHeight={110}
          color={graphColor}
          lineGradientStartColor={graphColor}
          startFillColor={graphColor}
          lineGradientEndColor={graphColor}
          endFillColor={graphColor}
        />
      </YStack>
    </BorderedCard>
  );
};
